import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import CalculatorReportBridge from "@/components/CalculatorReportBridge";
import type { KundaliResponse } from "@/lib/api/kundali";
import { NAKSHATRA_DASHA_LORD, DASHA_SEQUENCE, DASHA_DETAILS, formatNakshatra } from "./_data";

export { DashaSeoContent } from "./_seo-content";

export function DashaResult({
  result,
  defaultLanguage = "en",
}: {
  result: KundaliResponse;
  defaultLanguage?: "en" | "hi";
}) {
  const nakshatraKey = result.moon_nakshatra.toLowerCase().replace(/ /g, "_");
  const startingDasha = NAKSHATRA_DASHA_LORD[nakshatraKey];
  const startIdx = DASHA_SEQUENCE.findIndex((d) => d.lord === startingDasha?.lord);
  const orderedDashas =
    startIdx >= 0
      ? [...DASHA_SEQUENCE.slice(startIdx), ...DASHA_SEQUENCE.slice(0, startIdx)]
      : DASHA_SEQUENCE;
  const hi = defaultLanguage === "hi";
  const sourcePath = hi ? "/hi/dasha-calculator" : "/dasha-calculator";
  const bridgePlacement = hi
    ? "/hi/dasha-calculator_report_bridge"
    : "/dasha-calculator_report_bridge";

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12">
        <DashaHeader name={result.birth_details.name as string} />
        <MoonNakshatraCard result={result} startingDasha={startingDasha} />
        <DashaSequence orderedDashas={orderedDashas} />
        <CalculatorReportBridge
          planId="mahadasha_forecast"
          source={sourcePath}
          intent="chart_report"
          placement={bridgePlacement}
          title={
            hi
              ? "Mahadasha Forecast me 12-month timing dekhein"
              : "Turn this dasha sequence into a 12-month Mahadasha Forecast"
          }
          body={
            hi
              ? "Calculator cycle dikhata hai. Forecast me active mahadasha, antardasha transition, decision windows aur graha-specific upay chart se milte hain."
              : "The calculator shows the cycle. The forecast reads your active mahadasha, antardasha transitions, decision windows, and graha-specific upay from your chart."
          }
          cta={hi ? "Mahadasha Forecast paayein" : "Get Mahadasha Forecast"}
          language={defaultLanguage}
        />
        <CalculatorCrossLinks currentPage="dasha-calculator" />
      </div>
    </div>
  );
}

function DashaHeader({ name }: { name: string }) {
  return (
    <div className="text-center mb-8">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-ish-accent">
        Vimshottari Dasha
      </p>
      <h2 className="text-[26px] sm:text-[32px] font-bold leading-tight font-serif text-ish-dark">
        Dasha Periods for {name}
      </h2>
    </div>
  );
}

function MoonNakshatraCard({
  result,
  startingDasha,
}: {
  result: KundaliResponse;
  startingDasha: (typeof DASHA_SEQUENCE)[number] | undefined;
}) {
  const moonRashi = result.moon_rashi.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div className="card-sacred p-6 mb-6 text-center">
      <p className="text-[13px] font-semibold uppercase tracking-wide mb-2 text-ish-text-faint">
        Your Moon Nakshatra
      </p>
      <p className="text-[24px] font-bold font-serif text-ish-dark">
        {formatNakshatra(result.moon_nakshatra)}
      </p>
      <p className="text-[14px] mt-2 text-ish-text-muted">Moon in {moonRashi}</p>
      {startingDasha && (
        <div className="mt-4 inline-block px-4 py-2 rounded-lg text-[13px] font-semibold bg-ish-accent-light text-ish-accent">
          Starting Mahadasha Lord: {startingDasha.lord} ({startingDasha.years} years)
        </div>
      )}
    </div>
  );
}

function DashaSequence({ orderedDashas }: { orderedDashas: typeof DASHA_SEQUENCE }) {
  return (
    <div className="card-sacred p-5 sm:p-6 mb-6">
      <h3 className="text-[18px] font-bold mb-4 font-serif text-ish-dark">
        Your Mahadasha Sequence
      </h3>
      <p className="text-[13px] mb-5 text-ish-text-muted">
        The 120-year Vimshottari Dasha cycle begins from your Moon&apos;s nakshatra at birth. Each
        Mahadasha period is ruled by a specific planet.
      </p>
      <div className="space-y-3">
        {orderedDashas.map((dasha, i) => (
          <DashaPeriod key={dasha.lord} dasha={dasha} index={i} />
        ))}
      </div>
    </div>
  );
}

function DashaPeriod({ dasha, index }: { dasha: (typeof DASHA_SEQUENCE)[number]; index: number }) {
  const details = DASHA_DETAILS[dasha.lord];
  const isFirst = index === 0;
  return (
    <details className="group" open={isFirst}>
      <summary className="flex items-center justify-between cursor-pointer p-3 rounded-lg transition-colors hover:bg-stone-50">
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold bg-ish-accent-light text-ish-accent">
            {index + 1}
          </span>
          <span className="text-[14px] font-semibold text-ish-dark">
            {dasha.lord} Mahadasha
            {isFirst && <span className="ml-2 text-[11px]">Birth Dasha</span>}
          </span>
        </div>
        <span className="text-[13px] font-medium text-ish-text-faint">{dasha.years} years</span>
      </summary>
      {details && (
        <div className="pl-14 pr-3 pb-3 text-[13px] text-ish-text-muted">
          <p className="text-[12px] font-semibold mb-1 text-ish-text-faint">Significations</p>
          <p className="mb-2">{details.significations}</p>
          <p className="leading-relaxed">{details.nature}</p>
        </div>
      )}
    </details>
  );
}
